import { useSelector } from 'react-redux';
import "./index.scss";
import { currencyFormatter } from '../../../../utils/currency';
import { RootState } from '../../../../store';
import { Page } from '../../../../enums/page';
import React, { useMemo } from 'react';
import i18n from '../../../../utils/translations/i18n';
import { calculateThresholdStatus } from '../../../../utils/cart';
import { ReactComponent as CheckMark } from '../../../../assets/icons/check-mark.svg';
import PromoCouponGreen from '../../../../assets/icons/promo-coupon-green';
import CustomTooltip from '../../elements/customTooltip';
import { CouponState } from '../../../../enums/coupon';
import Cart from '../../../../models/cart';
import { get, getBrandKeywordCampaignParameters } from '../../../../utils/cookies';

interface CouponSummaryProps {
    removeCouponHandler?: () => void;
    cart?: Cart;
    doesDiscountExist?: boolean;
}

const CouponSummary: React.FC<CouponSummaryProps> = ({ removeCouponHandler, cart, doesDiscountExist }) => {
    const { applicationState: { currentPage, coupon, isCouponAppliedOnShipping,validatedCoupon } } = useSelector((state: RootState) => state);
    const { t } = i18n;
    let specialOfferCookie = getBrandKeywordCampaignParameters(get('np.brandKeywordCampaign')).couponCode;

    // Memoize the threshold status calculations
    const { remainingAmountCent, percentage } = useMemo(() => {
        return calculateThresholdStatus(cart, coupon?.thresholdValue);
    }, [cart, coupon?.thresholdValue]);

    // Determine the coupon status message based on current state
    const couponStatusMessage = useMemo(() => {
        if (currentPage !== Page.SHOPPINGBAG) return null;

        // If the discount is applied
        if (doesDiscountExist) {
            return (
                <div className='discount-applied'>
                    <CheckMark className="checkmark" />
                    <span className="discount-applied-text">{t('order_summary.code_applied')}</span>
                </div>
            );
        }

        // If the threshold value exists but is not met
        if (coupon?.state === CouponState.THRESHOLD_NOT_MET) {
            return (
                <div className="threshold">
                    <div className="more-needed">
                        <span className="more-needed-text">
                            {t('shopping_cart_details.add_amount').replace('[X]', currencyFormatter(remainingAmountCent, cart?.fractionDigits))}
                        </span>
                    </div>
                </div>
            );
        }

        // Fallback condition: No items eligible
        if (coupon?.state === CouponState.INELIGIBLE) {
            return (
                <div className='no-items-eligible'>
                    <span className='no-items-text text-red'>{`${coupon?.error ? coupon?.error : t('order_summary.no_items_eligible')}`}</span>
                </div>
            );
        }

    }, [coupon, cart?.discountExists, coupon?.thresholdValue, remainingAmountCent]);

    // Return null early if there is no coupon code
    if (!coupon?.code || (currentPage === Page.SHOPPINGBAG && !couponStatusMessage) || (coupon?.state === CouponState.INELIGIBLE && (validatedCoupon?.state === CouponState.INVALID || validatedCoupon?.state === CouponState.EXPIRED) && !specialOfferCookie)) {
        return null;
    }

    return (
        <React.Fragment>
            <div className={`coupon-summary-container ${(coupon?.code && coupon?.state === CouponState.INELIGIBLE && !specialOfferCookie && isCouponAppliedOnShipping && !doesDiscountExist && currentPage === Page.CHECKOUTPAYMENT) ? 'ineligible-code' : ''}`}>
                <div className='applied-code-container'>
                    <div className='coupon-details'>
                        <div className='coupon-title'>
                            <PromoCouponGreen width={11} height={11} className='promoIcon'/>
                            <span className="promo-title">{coupon?.code}</span>
                        </div>
                        {coupon?.description && <div className='coupon-desc'>
                            {coupon?.description}&nbsp;
                            {coupon?.details && 
                                <CustomTooltip content={coupon?.details} wrapperClasses="details-tooltip-wrapper" tooltipClasses="details-tooltip"><span className='discount-details link--underline'>{t('order_summary.details')}</span></CustomTooltip>
                            }
                        </div>}
                    </div>
                    {couponStatusMessage}
                </div>
                {currentPage === Page.SHOPPINGBAG && !doesDiscountExist && coupon?.state === CouponState.THRESHOLD_NOT_MET && (
                    <div className="progress-container">
                        <div className="progress-bar" style={{ width: `${percentage}%` }}></div>
                    </div>
                )}
                {(currentPage === Page.SHOPPINGBAG && doesDiscountExist) && (
                    <span className='change-promocode' onClick={removeCouponHandler}>{t('order_summary.change_promo')}</span>
                )}
            </div>
        </React.Fragment>
    );
};

export default CouponSummary;
